import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";

// One consistent rhythm for every band on the page, and a real <section>
// labelled by its own <h2> so the document outline makes sense to crawlers and
// screen readers alike.
export const Section = ({
  id,
  eyebrow,
  title,
  lead,
  banded = false,
  align = "center",
  children,
  maxWidth = "lg",
}) => {
  const reduced = useReducedMotion();
  const headingId = `${id}-heading`;

  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={title ? headingId : undefined}
      sx={{
        py: { xs: 8, md: 13 },
        backgroundColor: banded ? palette.bgAlt : "transparent",
      }}
    >
      <Container maxWidth={maxWidth}>
        {title && (
          <motion.div
            variants={stagger(0.08)}
            {...revealProps(reduced)}
            style={{
              textAlign: align,
              marginBottom: 56,
              maxWidth: align === "left" ? 720 : undefined,
            }}
          >
            {eyebrow && (
              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.primary,
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mb: 1.5,
                  }}
                >
                  {eyebrow}
                </Typography>
              </motion.div>
            )}
            <motion.div variants={fadeUp}>
              <Typography
                component="h2"
                id={headingId}
                sx={{
                  color: palette.text,
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.12,
                  fontSize: { xs: 30, sm: 38, md: 45 },
                }}
              >
                {title}
              </Typography>
            </motion.div>
            {lead && (
              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.textMuted,
                    fontSize: { xs: 16.5, md: 18.5 },
                    lineHeight: 1.65,
                    maxWidth: 640,
                    mx: align === "center" ? "auto" : 0,
                    mt: 2.5,
                  }}
                >
                  {lead}
                </Typography>
              </motion.div>
            )}
          </motion.div>
        )}
        {children}
      </Container>
    </Box>
  );
};

// Editorial, not boxed: content sits on the paper, separated by whitespace and
// a hairline rule. No borders, no panels, no shadows.
export const ruleSx = {
  borderTop: `1px solid ${palette.border}`,
  pt: { xs: 3.5, md: 4 },
  mt: { xs: 3.5, md: 4 },
};

export default Section;
